import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Challenge {
  id: number;
  icon: string;
  title: string;
  description: string;
  impact: string;
}

const ChallengeSection = () => {
  const challenges: Challenge[] = [
    {
      id: 1,
      icon: 'ExclamationTriangleIcon',
      title: 'Technology-First Thinking',
      description: 'Organizations invest heavily in AI tools without addressing the human factors that determine success or failure.',
      impact: '70% of AI initiatives stall due to lack of user adoption'
    },
    {
      id: 2,
      icon: 'UserGroupIcon',
      title: 'Resistance to Change',
      description: 'Employees fear job displacement and lack clarity on how AI will enhance their roles rather than replace them.',
      impact: 'Change resistance delays implementation by 6-12 months'
    },
    {
      id: 3,
      icon: 'ChartBarIcon',
      title: 'Misaligned Expectations',
      description: 'Leadership expects immediate ROI while underestimating the organizational transformation required for AI success.',
      impact: '60% of projects fail to meet initial business case projections'
    },
    {
      id: 4,
      icon: 'AcademicCapIcon',
      title: 'Skills Gap & Training',
      description: 'Teams lack the capabilities and confidence to work effectively with AI systems in their daily workflows.',
      impact: 'Productivity drops 40% during poorly managed transitions'
    },
    {
      id: 5,
      icon: 'CogIcon',
      title: 'Process Integration Failure',
      description: 'AI solutions are bolted onto existing processes rather than being thoughtfully integrated into workflows.',
      impact: 'Only 25% of AI tools achieve sustained usage after 6 months'
    },
    {
      id: 6,
      icon: 'ShieldExclamationIcon',
      title: 'Risk & Governance Concerns',
      description: 'Organizations struggle to balance innovation with ethical AI use, data privacy, and regulatory compliance.',
      impact: 'Governance gaps create legal and reputational risks'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-error/10 rounded-full mb-4">
            <Icon name="ExclamationCircleIcon" size={20} variant="solid" className="text-error" />
            <span className="text-sm font-semibold font-body text-error">The Challenge</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
            Why AI Transformations Struggle
          </h2>
          
          <p className="text-lg text-muted-foreground font-body">
            Most AI initiatives fail not because of technology limitations, but because organizations underestimate the human and organizational change required for successful adoption.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className="bg-card rounded-lg p-6 shadow-card hover:shadow-strong transition-all duration-300 border border-border hover:border-primary/20"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name={challenge.icon as any} size={24} variant="outline" className="text-error" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold font-headline text-foreground mb-2">
                    {challenge.title}
                  </h3>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground font-body mb-4 leading-relaxed">
                {challenge.description}
              </p>
              
              <div className="pt-4 border-t border-border">
                <div className="flex items-start gap-2">
                  <Icon name="ChartBarIcon" size={16} variant="solid" className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-xs font-medium text-warning font-body">
                    {challenge.impact}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 max-w-4xl mx-auto bg-gradient-to-r from-error/5 to-warning/5 rounded-lg p-8 border border-error/20">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="LightBulbIcon" size={32} variant="solid" className="text-error" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold font-headline text-foreground mb-2">
                The Real Problem: Change Management, Not Technology
              </h3>
              <p className="text-muted-foreground font-body">
                85% of AI transformation failures stem from inadequate change leadership, not technical shortcomings. Success requires addressing the human side of AI adoption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChallengeSection;